import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  FolderKanban,
  Inbox,
  Search,
} from 'lucide-react';
import {
  formatDuration,
  formatRelativeDate,
  formatShortDate,
  type PaginatedResponse,
  type Task,
} from '@oneup/shared';
import { tasksApi } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { TaskTagsControl } from '@/components/task-tags-control';
import { cn } from '@/lib/utils';
import { taskStatusVariant } from '@/lib/task-status';
import { contextItem, pageContext, useAlicePageContext } from '@/components/alice';
import { adminConfig } from '@/platform/config';
import { TaskDrawer } from './TaskDrawer';

export function ProjectsView() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { taskId } = useParams<{ taskId: string }>();
  const search = (searchParams.get('search') ?? '').trim();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());

  const { data, isLoading, error } = useQuery<PaginatedResponse<Task>>({
    queryKey: ['tasks', 'all'],
    queryFn: () => tasksApi.list({ limit: 500 }),
  });

  const flagMutation = useMutation({
    mutationFn: ({ id, commandCenterFlags }: { id: string; commandCenterFlags: string[] }) =>
      tasksApi.patch(id, { commandCenterFlags }),
    onMutate: async ({ id, commandCenterFlags }) => {
      await queryClient.cancelQueries({ queryKey: ['tasks', 'all'] });
      const previous = queryClient.getQueryData<PaginatedResponse<Task>>(['tasks', 'all']);
      if (previous) {
        queryClient.setQueryData<PaginatedResponse<Task>>(['tasks', 'all'], {
          ...previous,
          items: previous.items.map((task) =>
            task.id === id ? { ...task, commandCenterFlags } : task,
          ),
        });
      }
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(['tasks', 'all'], context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', 'all'] });
      queryClient.invalidateQueries({
        queryKey: ['schema', 'field-options', 'ENT_Projects', 'Command Center Flags'],
      });
    },
  });

  const tasks = data?.items ?? [];
  const tasksById = useMemo(() => new Map(tasks.map((task) => [task.id, task])), [tasks]);
  const parentTasks = useMemo(() => {
    const lowerSearch = search.toLowerCase();
    return tasks
      .filter((task) => (task.childTaskIds?.length ?? 0) > 0)
      .filter(isOpenTask)
      .filter((task) => !lowerSearch || matchesProjectSearch(task, lowerSearch, tasksById))
      .sort((a, b) => (b.statusLastModified ?? '').localeCompare(a.statusLastModified ?? ''));
  }, [search, tasks, tasksById]);

  const setSearch = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (value) params.set('search', value);
          else params.delete('search');
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const openTask = useCallback(
    (id: string) => {
      const qs = searchParams.toString();
      navigate(qs ? `/work/projects/${id}?${qs}` : `/work/projects/${id}`);
    },
    [navigate, searchParams],
  );

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectedProject = taskId ? tasksById.get(taskId) : undefined;
  const aliceContext = useMemo(
    () =>
      pageContext({
        portalKey: adminConfig.appId,
        pageKey: 'projects',
        pageTitle: 'Projects',
        recordType: taskId ? 'project' : undefined,
        recordId: taskId,
        recordLabel: selectedProject?.name ?? taskId,
        organizationLabel: selectedProject?.client?.name,
        contextItems: [
          contextItem('projects-page', 'page', 'Projects', 'Active projects grouped by parent task.'),
          ...(taskId
            ? [
                contextItem(
                  taskId,
                  'project',
                  selectedProject?.name ?? taskId,
                  selectedProject?.client?.name,
                ),
              ]
            : []),
        ],
      }),
    [selectedProject, taskId],
  );
  useAlicePageContext(aliceContext);

  if (isLoading) {
    return (
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <Card key={index} className="h-[220px] animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card className="flex items-start gap-3 p-6 text-destructive">
        <AlertCircle className="mt-0.5 h-4 w-4" aria-hidden />
        <div>
          <h2 className="text-base font-semibold">Could not load projects</h2>
          <p className="text-sm text-muted-foreground">
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-foreground">Projects</h1>
          <p className="text-[12px] text-muted-foreground">
            Parent task records with expandable subtasks.
          </p>
        </div>
        <div className="relative w-full max-w-[360px]">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search projects or subtasks"
            className="h-9 pl-8 text-[13px]"
          />
        </div>
      </div>

      {parentTasks.length === 0 ? (
        <Card className="flex flex-col items-center gap-2 px-4 py-12 text-center text-sm text-muted-foreground">
          <Inbox className="h-6 w-6 text-muted-foreground/50" aria-hidden />
          <span>No parent task records match this view.</span>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {parentTasks.map((project) => {
            const children = getChildTasks(project, tasksById).filter(isOpenTask);
            const expanded = expandedIds.has(project.id);
            return (
              <Card key={project.id} className="overflow-hidden">
                <div className="space-y-3 p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <FolderKanban className="h-5 w-5" aria-hidden />
                    </div>
                    <div className="min-w-0 flex-1">
                      <button
                        type="button"
                        onClick={() => openTask(project.id)}
                        className="line-clamp-2 text-left text-sm font-semibold text-foreground hover:text-primary"
                      >
                        {project.name}
                      </button>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        {project.status && (
                          <Badge variant={taskStatusVariant(project.semanticStatus)}>
                            {project.status}
                          </Badge>
                        )}
                        {project.priority && <Badge variant="muted">{project.priority}</Badge>}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-[11px]">
                    <Fact label="Subtasks" value={String(children.length)} />
                    <Fact label="Direct" value={formatDuration(project.trackedMinutes)} />
                    <Fact
                      label="Subtask"
                      value={formatDuration(project.subtaskTrackedMinutes)}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-[11px]">
                    <Fact
                      label="Total Time"
                      value={formatDuration(
                        project.totalTrackedWithSubtasksMinutes ?? project.trackedMinutes,
                      )}
                    />
                    <Fact label="Due" value={project.targetDate ? formatShortDate(project.targetDate) : '-'} />
                  </div>

                  <TaskTagsControl
                    value={project.commandCenterFlags}
                    disabled={flagMutation.isPending}
                    onChange={(commandCenterFlags) =>
                      flagMutation.mutate({ id: project.id, commandCenterFlags })
                    }
                  />

                  {project.client?.name && (
                    <div className="truncate text-[12px] text-muted-foreground">
                      {project.client.name}
                    </div>
                  )}

                  <div className="flex items-center justify-between border-t border-border pt-3">
                    <button
                      type="button"
                      onClick={() => toggleExpanded(project.id)}
                      className="inline-flex items-center gap-1.5 text-[12px] font-medium text-primary hover:underline"
                    >
                      {expanded ? (
                        <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5" aria-hidden />
                      )}
                      {expanded ? 'Hide subtasks' : 'Show subtasks'}
                    </button>
                    {project.statusLastModified && (
                      <span className="text-[11px] text-muted-foreground">
                        Updated {formatRelativeDate(project.statusLastModified)}
                      </span>
                    )}
                  </div>
                </div>

                {expanded && (
                  <div className="border-t border-border bg-muted/20">
                    {children.length === 0 ? (
                      <div className="px-4 py-3 text-[12px] text-muted-foreground">
                        No open subtasks loaded.
                      </div>
                    ) : (
                      <ul className="divide-y divide-border">
                        {children.map((child) => (
                          <li key={child.id}>
                            <button
                              type="button"
                              onClick={() => openTask(child.id)}
                              className={cn(
                                'block w-full px-4 py-3 text-left transition-colors hover:bg-muted/50',
                                taskId === child.id && 'bg-primary/5',
                              )}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="truncate text-[13px] font-medium text-foreground">
                                    {child.name}
                                  </div>
                                  <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                                    <span>{child.status || 'No status'}</span>
                                    <span>{formatDuration(child.trackedMinutes)}</span>
                                    {child.targetDate && <span>{formatShortDate(child.targetDate)}</span>}
                                  </div>
                                </div>
                                {child.commandCenterFlags[0] && (
                                  <Badge variant="warning">{child.commandCenterFlags[0]}</Badge>
                                )}
                              </div>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <div className="text-[11px] text-muted-foreground">
        Showing <span className="tabular-nums text-foreground">{parentTasks.length}</span> parent
        task records.
      </div>

      {taskId && (
        <TaskDrawer
          taskId={taskId}
          onClose={() => {
            const qs = searchParams.toString();
            navigate(qs ? `/work/projects?${qs}` : '/work/projects', { replace: true });
          }}
        />
      )}
    </div>
  );
}

function getChildTasks(project: Task, tasksById: Map<string, Task>): Task[] {
  return (project.childTaskIds ?? [])
    .map((id) => tasksById.get(id))
    .filter((task): task is Task => Boolean(task));
}

function matchesProjectSearch(task: Task, query: string, tasksById: Map<string, Task>): boolean {
  if (task.name.toLowerCase().includes(query)) return true;
  if (task.client?.name?.toLowerCase().includes(query)) return true;
  return getChildTasks(task, tasksById).some((child) => child.name.toLowerCase().includes(query));
}

function isOpenTask(task: Task): boolean {
  const status = task.status.trim().toLowerCase();
  return task.semanticStatus !== 'done' && status !== 'closed' && status !== 'archived' && status !== 'done';
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-background px-2 py-1.5">
      <div className="text-[9px] font-semibold uppercase tracking-[0.5px] text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 truncate text-[12px] font-medium text-foreground">{value}</div>
    </div>
  );
}
